import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus, X, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

interface ProductMaterialTemplatesTabProps {
  productId: string;
}

interface MaterialTemplate {
  id: string;
  material_id: string;
  default_quantity: number;
  default_width: number;
  default_length: number;
  default_thickness: number;
  notes: string | null;
  position: number;
  materials: {
    id: string;
    name: string;
    sku: string | null;
    category: string | null;
    unit: string;
    unit_cost: number;
  };
}

export function ProductMaterialTemplatesTab({ productId }: ProductMaterialTemplatesTabProps) {
  const queryClient = useQueryClient();
  const [isAdding, setIsAdding] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  
  // Form state
  const [selectedMaterialId, setSelectedMaterialId] = useState("");
  const [quantity, setQuantity] = useState("1");
  const [width, setWidth] = useState("");
  const [length, setLength] = useState("");
  const [thickness, setThickness] = useState("");
  const [notes, setNotes] = useState("");

  // Fetch product material templates
  const { data: templates, isLoading } = useQuery({
    queryKey: ["product-material-templates", productId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("product_material_templates")
        .select(`
          *,
          materials (
            id,
            name,
            sku,
            category,
            unit,
            unit_cost
          )
        `)
        .eq("product_id", productId)
        .order("position");
      
      if (error) throw error;
      return data as MaterialTemplate[];
    },
    enabled: !!productId,
  });

  // Fetch available materials
  const { data: materials } = useQuery({
    queryKey: ["materials"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("materials")
        .select("*")
        .eq("is_active", true)
        .is("deleted_at", null)
        .order("name");
      
      if (error) throw error;
      return data;
    },
  });

  // Add template mutation
  const addTemplateMutation = useMutation({
    mutationFn: async () => {
      const { error } = await supabase
        .from("product_material_templates")
        .insert({
          product_id: productId,
          material_id: selectedMaterialId,
          default_quantity: parseFloat(quantity),
          default_width: parseFloat(width),
          default_length: parseFloat(length),
          default_thickness: parseFloat(thickness),
          notes: notes || null,
          position: (templates?.length || 0) + 1,
        });
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-material-templates", productId] });
      toast.success("Materiaali lisätty");
      resetForm();
    },
    onError: (error) => {
      toast.error("Virhe: " + error.message);
    },
  });

  // Update template mutation
  const updateTemplateMutation = useMutation({
    mutationFn: async () => {
      if (!editingId) return;
      const { error } = await supabase
        .from("product_material_templates")
        .update({
          default_quantity: parseFloat(quantity),
          default_width: parseFloat(width),
          default_length: parseFloat(length),
          default_thickness: parseFloat(thickness),
          notes: notes || null,
        })
        .eq("id", editingId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-material-templates", productId] });
      toast.success("Materiaali päivitetty");
      resetForm();
    },
    onError: (error) => {
      toast.error("Virhe: " + error.message);
    },
  });

  // Delete template mutation
  const deleteTemplateMutation = useMutation({
    mutationFn: async (templateId: string) => {
      const { error } = await supabase
        .from("product_material_templates")
        .delete()
        .eq("id", templateId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["product-material-templates", productId] });
      toast.success("Materiaali poistettu");
    },
    onError: (error) => {
      toast.error("Virhe: " + error.message);
    },
  });

  const resetForm = () => {
    setIsAdding(false);
    setEditingId(null);
    setSelectedMaterialId("");
    setQuantity("1");
    setWidth("");
    setLength("");
    setThickness("");
    setNotes("");
  };

  const handleEdit = (template: MaterialTemplate) => {
    setEditingId(template.id);
    setIsAdding(true);
    setSelectedMaterialId(template.material_id);
    setQuantity(template.default_quantity.toString());
    setWidth(template.default_width.toString());
    setLength(template.default_length.toString());
    setThickness(template.default_thickness.toString());
    setNotes(template.notes || "");
  };

  const handleSubmit = () => {
    if (editingId) {
      updateTemplateMutation.mutate();
    } else {
      addTemplateMutation.mutate();
    }
  };

  if (isLoading) {
    return <div className="text-center py-8">Ladataan...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="font-semibold">Oletusmateriaalit</h3>
          <p className="text-sm text-muted-foreground">
            Määritä tuotteen vakiomateriaalit, jotka kopioituvat automaattisesti tilaukselle
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={() => {
            if (isAdding) {
              resetForm();
            } else {
              setIsAdding(true);
            }
          }}
        >
          {isAdding ? <X className="h-4 w-4 mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
          {isAdding ? "Peruuta" : "Lisää materiaali"}
        </Button>
      </div>

      {isAdding && (
        <div className="border rounded-lg p-4 space-y-4 bg-muted/50">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Materiaali *</Label>
              <Select
                value={selectedMaterialId}
                onValueChange={setSelectedMaterialId}
                disabled={!!editingId}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Valitse materiaali" />
                </SelectTrigger>
                <SelectContent>
                  {materials?.map((material) => (
                    <SelectItem key={material.id} value={material.id}>
                      {material.name} ({material.sku || "ei SKU"})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Oletusmäärä *</Label>
              <Input
                type="number"
                step="0.001"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                placeholder="1.000"
              />
            </div>

            <div className="space-y-2">
              <Label>Leveys (m) *</Label>
              <Input
                type="number"
                step="0.001"
                value={width}
                onChange={(e) => setWidth(e.target.value)}
                placeholder="0.000"
              />
            </div>

            <div className="space-y-2">
              <Label>Pituus (m) *</Label>
              <Input
                type="number"
                step="0.001"
                value={length}
                onChange={(e) => setLength(e.target.value)}
                placeholder="0.000"
              />
            </div>

            <div className="space-y-2">
              <Label>Paksuus (m) *</Label>
              <Input
                type="number"
                step="0.0001"
                value={thickness}
                onChange={(e) => setThickness(e.target.value)}
                placeholder="0.0000"
              />
            </div>

            <div className="space-y-2">
              <Label>Muistiinpanot</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Lisätiedot..."
                rows={3}
              />
            </div>
          </div>

          <Button
            type="button"
            onClick={handleSubmit}
            disabled={
              !selectedMaterialId ||
              !quantity ||
              !width ||
              !length ||
              !thickness ||
              addTemplateMutation.isPending ||
              updateTemplateMutation.isPending
            }
          >
            {editingId ? "Tallenna muutokset" : "Lisää materiaali"}
          </Button>
        </div>
      )}

      {!templates || templates.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground border rounded-lg">
          Ei oletusmateriaalia. Lisää materiaaleja yllä olevalla painikkeella.
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Materiaali</TableHead>
              <TableHead>SKU</TableHead>
              <TableHead className="text-right">Määrä</TableHead>
              <TableHead className="text-right">Leveys (m)</TableHead>
              <TableHead className="text-right">Pituus (m)</TableHead>
              <TableHead className="text-right">Paksuus (m)</TableHead>
              <TableHead>Muistiinpanot</TableHead>
              <TableHead className="text-right">Toiminnot</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {templates.map((template) => (
              <TableRow key={template.id}>
                <TableCell className="font-medium">{template.materials.name}</TableCell>
                <TableCell className="font-mono text-sm">
                  {template.materials.sku || "-"}
                </TableCell>
                <TableCell className="text-right">{template.default_quantity.toFixed(3)}</TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {template.default_width.toFixed(3)}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {template.default_length.toFixed(3)}
                </TableCell>
                <TableCell className="text-right font-mono text-sm">
                  {template.default_thickness.toFixed(4)}
                </TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  {template.notes || "-"}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => handleEdit(template)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => deleteTemplateMutation.mutate(template.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

    </div>
  );
}